import { useCallback, useSyncExternalStore } from 'react';

const YEAR_IN_MS = 365 * 24 * 60 * 60 * 1000;

function getCookie(name: string | null | undefined): string | null {
  if (!name) {
    return null;
  }

  const cookies = document.cookie.split(';');

  for (const cookie of cookies) {
    const [cookieName, cookieValue] = cookie
      .split('=')
      .map((part) => part.trim());
    if (cookieName === name) {
      return decodeURIComponent(cookieValue || '');
    }
  }

  return null;
}

function subscribeToCookieChanges(callback: () => void): () => void {
  if (typeof cookieStore !== 'undefined') {
    cookieStore.addEventListener('change', callback);

    return () => {
      cookieStore.removeEventListener('change', callback);
    };
  }

  return () => {};
}

export function useCookie(
  name: string | null | undefined
): [value: string | null, setValue: (value: string | null) => void] {
  const value = useSyncExternalStore(
    subscribeToCookieChanges,
    () => getCookie(name),
    () => null
  );

  const setValue = useCallback(
    (newValue: string | null) => {
      if (!name) {
        return;
      }

      if (newValue === null) {
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      } else {
        const expires = new Date(Date.now() + YEAR_IN_MS).toUTCString();
        document.cookie = `${name}=${encodeURIComponent(newValue)}; path=/; expires=${expires}`;
      }
    },
    [name]
  );

  return [value, setValue];
}
